import React, { useState, useEffect } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import BilingualInput from '../../components/admin/BilingualInput';
import BilingualTextArea from '../../components/admin/BilingualTextArea';
import ClientsEditorFields from '../../components/admin/ClientsEditorFields';
import ClientLogoUploadGrid from '../../components/admin/ClientLogoUploadGrid';
import ServiceCardManager from '../../components/admin/ServiceCardManager';
import { useNavigate } from 'react-router-dom';
import {
    Building2,
    Home,
    Type,
    PanelBottom,
    Mail,
    HardHat,
    DollarSign,
    Zap,
    LogOut,
    Loader2,
    CheckCircle2,
    Save,
    Layout,
    FileText,
    Image as ImageIcon,
    Mountain,
    Factory,
    ShieldCheck,
    Handshake,
    Ship,
    Boxes,
    Plus,
    Trash2,
    RefreshCw,
    AlertCircle,
    ChevronDown,
    ChevronUp,
    MapPin,
    Target,
    Plane,
    Truck,
    Info,
    Briefcase,
    Wrench,
    Map,
    Upload
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const defaultMiningImages: string[] = [
  "/mining/DSC00193.JPG", "/mining/DSC00194.JPG", "/mining/DSC00195.JPG", "/mining/DSC00196.JPG",
  "/mining/DSC00197.JPG", "/mining/DSC00198.JPG", "/mining/DSC00199.JPG", "/mining/DSC00200.JPG",
  "/mining/DSC00201.JPG", "/mining/DSC00202.JPG", "/mining/DSC00203.JPG", "/mining/DSC00204.JPG",
  "/mining/DSC00205.JPG", "/mining/DSC00206.JPG", "/mining/DSC00207.JPG", "/mining/DSC00208.JPG",
  "/mining/DSC00209.JPG", "/mining/DSC00210.JPG", "/mining/DSC00211.JPG", "/mining/DSC00212.JPG",
  "/mining/DSC00213.JPG", "/mining/DSC00214.JPG", "/mining/DSC00215.JPG", "/mining/DSC00216.JPG",
  "/mining/DSC00217.JPG", "/mining/DSC00218.JPG", "/mining/DSC00219.JPG", "/mining/DSC00220.JPG",
  "/mining/DSC00221.JPG", "/mining/DSC00222.JPG", "/mining/DSC00223.JPG", "/mining/DSC00224.JPG",
  "/mining/DSC00225.JPG", "/mining/DSC00226.JPG", "/mining/DSC00227.JPG", "/mining/DSC00232.JPG",
  "/mining/DSC00228.JPG", "/mining/DSC00229.JPG", "/mining/DSC00230.JPG", "/mining/DSC00231.JPG",
  "/mining/DSC00233.JPG", "/mining/DSC00234.JPG", "/mining/DSC00235.JPG", "/mining/DSC00236.JPG",
  "/mining/DSC00237.JPG", "/mining/DSC00238.JPG", "/mining/DSC00239.JPG", "/mining/DSC00240.JPG",
  "/mining/DSC00241.JPG", "/mining/DSC00242.JPG", "/mining/DSC00243.JPG", "/mining/DSC00244.JPG",
  "/mining/DSC00245.JPG", "/mining/DSC00246.JPG", "/mining/DSC00247.JPG", "/mining/DSC00248.JPG",
  "/mining/DSC00249.JPG", "/mining/DSC00250.JPG", "/mining/DSC00251.JPG", "/mining/DSC00252.JPG",
  "/mining/DSC00253.JPG", "/mining/DSC00254.JPG", "/mining/DSC00255.JPG", "/mining/DSC00256.JPG",
  "/mining/DSC00257.JPG", "/mining/DSC00258.JPG", "/mining/DSC00259.JPG", "/mining/DSC00260.JPG",
  "/mining/DSC00261.JPG", "/mining/DSC00262.JPG", "/mining/DSC00301.JPG"
];

const AdminMining: React.FC = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'content' | 'images'>('content');
    const [activeSection, setActiveSection] = useState<'banner' | 'about' | 'services' | 'operations' | 'products' | 'branches' | 'clients' | 'gallery' | 'contact'>('banner');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState<string | null>(null);
    const [content, setContent] = useState<any>({ Mining: {} });
    const [pendingImages, setPendingImages] = useState<{ [key: string]: { file: File, preview: string } }>({});
    const [message, setMessage] = useState<{ text: string, type: 'success' | 'error' | '' }>({ text: '', type: '' });
    const [activeMiningBranches, setActiveMiningBranches] = useState<number[]>([]);
    const username = localStorage.getItem('adminUser') || 'Admin';

    useEffect(() => {
        const token = localStorage.getItem('adminToken');
        if (!token) {
            navigate('/admin/login');
        } else {
            fetchContent();
        }
    }, [navigate]);

    const fetchContent = async () => {
        setLoading(true);
        try {
            const response = await fetch('/api/homepage/content');
            const data = await response.json();
            if (!data.Mining) data.Mining = {};
            setContent(data);
            if (data.Mining) {
                const ids = [];
                for (let i = 1; i <= 40; i++) {
                    if (data.Mining[`br_${i}_name`]) {
                        ids.push(i);
                    }
                }
                setActiveMiningBranches(ids);
            }
        } catch (err) {
            console.error('Failed to fetch content');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/admin/login');
    };

    const updateField = (key: string, value: any) => {
        setContent((prev: any) => ({
            ...prev,
            Mining: {
                ...prev.Mining,
                [key]: value
            }
        }));
    };

    const handleDeleteGalleryKey = async (key: string) => {
        if (!window.confirm('Delete this gallery image?')) return;
        try {
            setSaving('gallery');
            const token = localStorage.getItem('adminToken');
            await fetch('/api/homepage/delete-key', {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ section: 'Mining', content_key: key })
            });
            setContent((prev: any) => {
                const copy = { ...prev.Mining };
                delete copy[key];
                return { ...prev, Mining: copy };
            });
            setMessage({ text: 'Gallery image deleted.', type: 'success' });
        } catch (err) {
            console.error(err);
            setMessage({ text: 'Failed to delete image.', type: 'error' });
        } finally {
            setSaving(null);
            setTimeout(() => setMessage({ text: '', type: '' }), 3000);
        }
    };

    const handleUploadGalleryImage = async (key: string, file: File) => {
        try {
            setSaving('gallery');
            const formData = new FormData();
            formData.append('section', 'Mining');
            formData.append('content_key', key);
            formData.append('image', file);

            const response = await fetch('/api/homepage/update-image', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                },
                body: formData
            });

            if (response.ok) {
                const data = await response.json();
                updateField(key, data.imageUrl);
                setMessage({ text: 'Image uploaded successfully!', type: 'success' });
            } else {
                setMessage({ text: 'Failed to upload image.', type: 'error' });
            }
        } catch (err) {
            console.error(err);
            setMessage({ text: 'Error uploading image.', type: 'error' });
        } finally {
            setSaving(null);
            setTimeout(() => setMessage({ text: '', type: '' }), 3000);
        }
    };

    const handleAddGalleryImage = () => {
        const newKey = `gallery_img_${Date.now()}`;
        updateField(newKey, '');
    };

    const saveContent = async () => {
        setSaving('Mining');
        try {
            const miningData = content.Mining || {};
            // Gather all fields from the active section to ensure they are saved even if using fallbacks
            const fieldsToSave: { [key: string]: string } = {};

            if (activeSection === 'banner') {
                fieldsToSave['banner_title'] = miningData.banner_title ?? 'Hamdan Mining';
                fieldsToSave['banner_subtitle'] = miningData.banner_subtitle ?? 'Part of one of the largest corporate conglomerates from the sultanate of oman';
            } else if (activeSection === 'about') {
                fieldsToSave['about_title'] = miningData.about_title ?? 'Hamdan Mining International';
                fieldsToSave['about_desc'] = miningData.about_desc ?? 'Hamdan Mining International is a leading mining and aggregate-producing enterprise based in the Sultanate of Oman. Equipped with state-of-the-art 600 TPH automated three-stage crushing plants, the company operates high-capacity processing facilities at the Liwa Quarry. Our primary operations focus on the production and export of high-quality Gabbro aggregates, sand, and rock materials that meet the stringent technical requirements of major infrastructure and construction projects.';
                fieldsToSave['about_website'] = miningData.about_website ?? 'www.hamdanmining.com';
                [1, 2, 3].forEach(id => {
                    fieldsToSave[`about_pt_${id}_icon`] = miningData[`about_pt_${id}_icon`] ?? (id === 1 ? 'Factory' : id === 2 ? 'ShieldCheck' : 'Globe2');
                    fieldsToSave[`about_pt_${id}_title`] = miningData[`about_pt_${id}_title`] ?? (id === 1 ? 'Production Capacity' : id === 2 ? 'HSE & Quality' : 'Export Markets');
                    fieldsToSave[`about_pt_${id}_desc`] = miningData[`about_pt_${id}_desc`] ?? (id === 1 ? '600 TPH automated three-stage crushing plants.' : id === 2 ? 'Integrated HSE and quality management systems.' : 'Exports to Qatar, Bahrain, Kuwait, and Bangladesh.');
                });
            } else if (activeSection === 'services') {
                fieldsToSave['services_title'] = miningData.services_title ?? 'Our Services';
                [1, 2, 3].forEach(id => {
                    fieldsToSave[`svc_${id}_title`] = miningData[`svc_${id}_title`] ?? '';
                    fieldsToSave[`svc_${id}_desc`] = miningData[`svc_${id}_desc`] ?? '';
                });
            } else if (activeSection === 'operations') {
                ['prod', 'hse', 'cust', 'exp'].forEach(id => {
                    const labels: any = { prod: 'Production Capacity & Operations', hse: 'Commitment to HSE & Quality', cust: 'Customer Focus & Service Reliability', exp: 'Export & Market Coverage' };
                    const defs: any = {
                        prod: 'Hamdan Mining International owns and operates high-capacity processing facilities at the Liwa Quarry. Our primary operations focus on the production of high-quality Gabbro aggregates, sand, and rock materials.',
                        hse: 'We maintain rigorous health, safety, and environment standards across all our mining and processing sites. Our automated systems ensure consistent quality that meets international standards.',
                        cust: 'We prioritize long-term partnerships through consistent quality and timely delivery of materials for large-scale infrastructure projects.',
                        exp: 'Our mining products are exported to major markets across the GCC, including Qatar, Bahrain, Kuwait, and South Asian markets like Bangladesh.'
                    };
                    fieldsToSave[`${id}_title`] = miningData[`${id}_title`] ?? labels[id];
                    fieldsToSave[`${id}_desc`] = miningData[`${id}_desc`] ?? defs[id];
                });
            } else if (activeSection === 'products') {
                const products = [
                    { t: '0–2 mm Manufactured Sand (Fine Aggregate)', d: 'Precisely graded fine material suitable for asphalt production, concrete mix optimization, block manufacturing, and specialized construction applications.', c: 'Sand' },
                    { t: '0–5 mm Manufactured Sand (Coarse Fine Aggregate)', d: 'Well-processed crushed sand ideal for ready-mix concrete, plastering works, pavement layers, and high-performance concrete applications requiring controlled fines content.', c: 'Sand' },
                    { t: '5–10 mm Crushed Aggregate', d: 'Clean, uniformly graded aggregate used for high-strength concrete mixes, premix asphalt layers, and general construction applications requiring fine coarse aggregates.', c: 'Aggregate' },
                    { t: '10–20 mm Crushed Aggregate', d: 'Standard coarse aggregate widely used in structural concrete, road base reinforcement, and infrastructure projects requiring consistent particle size and angularity.', c: 'Aggregate' },
                    { t: '20–28 mm Crushed Aggregate', d: 'Intermediate-sized aggregate suitable for heavy-duty concrete works, drainage layers, and load-bearing structural applications.', c: 'Aggregate' },
                    { t: '20–40 mm Crushed Aggregate', d: 'Large-sized aggregate designed for mass concrete structures, foundation works, and applications requiring enhanced void ratios and superior drainage performance.', c: 'Aggregate' },
                    { t: 'Engineered Backfill Material', d: 'Well-graded and compactable material used for structural backfilling, trench filling, retaining wall support, and civil engineering works requiring controlled compaction properties.', c: 'Road Materials' },
                    { t: 'Subgrade Material', d: 'Prepared natural/crushed material used to improve ground bearing capacity, provide uniform support for pavement structures, and enhance long-term soil stability.', c: 'Road Materials' },
                    { t: 'Granular Sub-Base (GSB) Material', d: 'High-quality, mechanically crushed granular material used as a foundational layer in road construction, providing load dispersion, drainage, and structural support for upper pavement layers.', c: 'Road Materials' },
                    { t: 'Armour Rock (Riprap)', d: 'Heavy-duty, large-dimension rock blocks used for shoreline protection, breakwaters, revetments, and erosion control systems requiring high durability and mass stability.', c: 'Rock & Filter' },
                    { t: 'Filter Material', d: 'Carefully graded aggregate used in drainage systems, geotechnical filtration layers, and hydraulic structures to prevent soil migration while permitting controlled water flow.', c: 'Rock & Filter' }
                ];
                products.forEach((p, idx) => {
                    fieldsToSave[`prod_${idx + 1}_title`] = miningData[`prod_${idx + 1}_title`] ?? p.t;
                    fieldsToSave[`prod_${idx + 1}_desc`] = miningData[`prod_${idx + 1}_desc`] ?? p.d;
                    fieldsToSave[`prod_${idx + 1}_cat`] = miningData[`prod_${idx + 1}_cat`] ?? p.c;
                });
            } else if (activeSection === 'clients') {
                fieldsToSave['clients_title'] = miningData.clients_title ?? 'Our Clients';
                fieldsToSave['clients_desc'] = miningData.clients_desc ?? 'Hamdan Mining International has supplied high-quality materials to a wide range of companies and organizations.';
                fieldsToSave['clients_list'] = miningData.clients_list ?? 'Complete Trading Solutions\nAggregates Manufacturing Plant & Trading FZE\nAl Ali Engineering Contracting & Trading Co. WLL\nAl Makarim Trading LLC\nAl Jood Natural Resources\nBL Khadem International Enterprises LLC\nAL Hodaifi Rocks\nMFH Company\nHassan Jumma Backer Trading & Contracting Co. LLC\nGalfar Engineering & Contracting SAOG\nAl Makarim Trading Enterprise\nAl Tasnim Enterprise LLC\nAl-Awalia (QPMC)\nHamdan Alalamia For Gabro';
                fieldsToSave['clients_logo_urls'] = miningData.clients_logo_urls ?? '';
                fieldsToSave['clients_footer'] = miningData.clients_footer ?? '';
                fieldsToSave['clients_footer_desc'] = miningData.clients_footer_desc ?? '';
            } else if (activeSection === 'gallery') {
                fieldsToSave['gallery_title'] = miningData.gallery_title ?? 'Hamdan Mining Gallery';
                fieldsToSave['gallery_subtitle'] = miningData.gallery_subtitle ?? 'A visual overview of Hamdan Mining operations, production facilities, and site activities.';
                // Only save actual image keys — exclude _ar and _ar_ar suffix junk rows
                Object.keys(miningData)
                    .filter(k => k.startsWith('gallery_img_') && !k.endsWith('_ar') && miningData[k]?.trim())
                    .forEach(k => {
                        fieldsToSave[k] = miningData[k];
                    });
            } else if (activeSection === 'contact') {
                fieldsToSave['contact_form_title'] = miningData.contact_form_title ?? 'Send Us Email';
                fieldsToSave['contact_info_title'] = miningData.contact_info_title ?? 'Get in Touch';
                fieldsToSave['contact_info_desc'] = miningData.contact_info_desc ?? 'We appreciate your interest in Hamdan Mining, and we want to make it as easy as possible for you to get your queries answered.';
                fieldsToSave['contact_address'] = miningData.contact_address ?? 'Salalah, Sultanate of Oman';
                fieldsToSave['contact_phone'] = miningData.contact_phone ?? '+968 23-210000';
                fieldsToSave['contact_fax'] = miningData.contact_fax ?? '+(968) 23212666';
                fieldsToSave['contact_email'] = miningData.contact_email ?? 'info@hamdanmining.com';
            } else if (activeSection === 'branches') {
                for (let i = 1; i <= 40; i++) {
                    if (miningData[`br_${i}_name`] === undefined) continue;
                    ['name', 'addr', 'phone', 'fax', 'coord'].forEach(f => {
                        fieldsToSave[`br_${i}_${f}`] = miningData[`br_${i}_${f}`] ?? '';
                    });
                }
            }

            const promises = Object.entries(fieldsToSave).map(async ([key, value]) => {
                const response = await fetch('/api/homepage/update-text', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                    },
                    body: JSON.stringify({
                        section: 'Mining',
                        content_key: key,
                        content_value: value,
                        content_value_ar: miningData[`${key}_ar`] || null
                    })
                });

                if (!response.ok) {
                    const errText = await response.text();
                    console.error(`Field update failed for ${key}: status ${response.status}`, errText);
                }
                return response;
            });

            const results = await Promise.all(promises);
            const allOk = results.every(r => r.ok);
            if (allOk) {
                setMessage({ text: `${activeSection} section updated successfully!`, type: 'success' });
                fetchContent();
            } else {
                const failed = results.filter(r => !r.ok).length;
                setMessage({ text: `${failed} fields failed to save. Check console for details.`, type: 'error' });
            }
        } catch (err) {
            console.error('Save failed', err);
            setMessage({ text: 'Failed to connect to server. Check your connection.', type: 'error' });
        } finally {
            setTimeout(() => {
                setSaving(null);
                setTimeout(() => setMessage({ text: '', type: '' }), 3000);
            }, 500);
        }
    };

    const handleImageSelect = (key: string, file: File) => {
        const preview = URL.createObjectURL(file);
        setPendingImages(prev => ({
            ...prev,
            [key]: { file, preview }
        }));
    };

    const saveImages = async () => {
        setSaving('images');
        setMessage({ text: 'Starting image synchronization...', type: '' });
        try {
            const promises = Object.entries(pendingImages).map(async ([key, pending]) => {
                const formData = new FormData();
                formData.append('section', 'Mining');
                formData.append('content_key', key);
                formData.append('image', pending.file);

                const response = await fetch('/api/homepage/update-image', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                    },
                    body: formData
                });

                if (!response.ok) {
                    const errText = await response.text();
                    console.error(`Upload failed for ${key}:`, errText);
                }
                return response;
            });

            const results = await Promise.all(promises);
            const allOk = results.every(r => r.ok);

            if (allOk) {
                setMessage({ text: 'All images synchronized successfully', type: 'success' });
                setPendingImages({});
                fetchContent();
            } else {
                setMessage({ text: 'Some image uploads failed. Check console for details.', type: 'error' });
            }
        } catch (err) {
            console.error('Image upload failed', err);
            setMessage({ text: 'Connectivity error. Check your server status.', type: 'error' });
        } finally {
            setSaving(null);
            setTimeout(() => setMessage({ text: '', type: '' }), 4000);
        }
    };

    const sections = [
        { id: 'banner', label: 'Hero Banner', icon: Layout },
        { id: 'about', label: 'About Us', icon: FileText },
        { id: 'services', label: 'Services', icon: Wrench },
        { id: 'operations', label: 'Operations', icon: Factory },
        { id: 'products', label: 'Products', icon: Boxes },
        { id: 'branches', label: 'Branch Network', icon: Map },
        { id: 'clients', label: 'Clients', icon: Handshake },
        { id: 'gallery', label: 'Gallery', icon: ImageIcon },
        { id: 'contact', label: 'Contact', icon: Mail },
    ];

    const UploadBox = ({ keyName, label, currentImage }: any) => {
        const pending = pendingImages[keyName];
        const displayImage = pending ? pending.preview : currentImage;

        return (
            <div className="group bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:border-blue-200 transition-all flex flex-col">
                <div className="flex justify-between items-center mb-4">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{label}</span>
                    {currentImage && !pending && (
                        <div className="flex items-center gap-1 text-emerald-500 bg-emerald-50 px-2 py-0.5 rounded-lg text-[8px] font-bold uppercase">
                            <CheckCircle2 className="w-3 h-3" /> Live
                        </div>
                    )}
                </div>
                <div className="relative aspect-video bg-slate-50 rounded-xl overflow-hidden mb-4 border border-slate-100">
                    {displayImage ? (
                        <img src={displayImage} className="w-full h-full object-cover" alt="Preview" />
                    ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-slate-300">
                            <ImageIcon className="w-8 h-8 opacity-20" />
                        </div>
                    )}
                    <label className="absolute inset-0 cursor-pointer bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-[1px]">
                        <ImageIcon className="w-6 h-6 text-white" />
                        <input type="file" className="hidden" accept="image/*" onChange={e => e.target.files?.[0] && handleImageSelect(keyName, e.target.files[0])} />
                    </label>
                </div>
                {pending && <div className="text-[8px] text-amber-500 font-bold uppercase text-center">Unsaved Changes</div>}
            </div>
        );
    };

    return (
        <div className="flex h-screen bg-slate-50 font-sans overflow-hidden">
            <AdminSidebar />

            {/* Main Area */}
            <div className="flex-1 flex flex-col h-full bg-slate-50/50">
                {/* Header */}
                <header className="h-20 bg-white border-b border-slate-200 flex items-center justify-between px-10 shadow-sm shrink-0 z-30">
                    <div className="flex items-center space-x-6">
                        <h2 className="text-xl font-extrabold text-slate-800 flex items-center gap-2">
                            <Mountain className="w-6 h-6 text-blue-600" /> Mining Editor
                        </h2>

                        <div className="flex bg-slate-100 p-1.5 rounded-2xl">
                            <button
                                onClick={() => setActiveTab('content')}
                                className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'content' ? 'bg-white text-blue-600 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                <Type className="w-4 h-4" />
                                <span>Content Details</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('images')}
                                className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'images' ? 'bg-white text-blue-600 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                <ImageIcon className="w-4 h-4" />
                                <span>Media Assets</span>
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center space-x-6">
                        <div className="hidden sm:block text-right">
                            <div className="text-sm font-bold text-slate-900">{username}</div>
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Portal Manager</div>
                        </div>
                        <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center border border-slate-200 shadow-inner">
                            <CheckCircle2 className="w-5 h-5 text-blue-600" />
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-y-auto p-10 custom-scrollbar scroll-smooth">
                    <div className="max-w-6xl mx-auto">
                        <AnimatePresence mode="wait">
                            {activeTab === 'content' ? (
                                <motion.div key="content" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-8">
                                    {/* Section Navigation */}
                                    <div className="flex flex-wrap gap-3 mb-8">
                                        {sections.map(sec => (
                                            <button
                                                key={sec.id}
                                                onClick={() => setActiveSection(sec.id as any)}
                                                className={`flex items-center space-x-2 px-5 py-2.5 rounded-xl font-bold text-xs transition-all border ${activeSection === sec.id ? 'bg-blue-600 border-blue-600 text-white shadow-lg' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'}`}
                                            >
                                                <sec.icon className="w-4 h-4" />
                                                <span>{sec.label}</span>
                                            </button>
                                        ))}
                                    </div>

                                    {message.text && (
                                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className={`p-4 rounded-2xl font-bold text-sm flex items-center gap-3 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                                            {message.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                                            {message.text}
                                        </motion.div>
                                    )}

                                    <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm">
                                        <div className="flex items-center justify-between mb-8">
                                            <h3 className="text-lg font-bold text-slate-800 flex items-center capitalize">
                                                <div className="w-1.5 h-6 bg-blue-600 rounded-full mr-3" />
                                                {activeSection} Configuration
                                            </h3>
                                            <button
                                                onClick={saveContent}
                                                disabled={!!saving}
                                                className="flex items-center space-x-2 px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-200 text-white rounded-xl font-bold text-sm transition-all shadow-lg active:scale-95"
                                            >
                                                {saving === 'Mining' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                                <span>Update {activeSection}</span>
                                            </button>
                                        </div>

                                        {/* Dynamic Section Forms */}
                                        {activeSection === 'banner' && (
                                            <div className="space-y-6">
                                                <BilingualInput 
                                                    label="Banner Title"
                                                    englishValue={content.Mining?.banner_title}
                                                    arabicValue={content.Mining?.banner_title_ar}
                                                    onChangeEnglish={val => updateField('banner_title', val)}
                                                    onChangeArabic={val => updateField('banner_title_ar', val)}
                                                />
                                                <BilingualTextArea 
                                                    label="Banner Subtitle"
                                                    englishValue={content.Mining?.banner_subtitle}
                                                    arabicValue={content.Mining?.banner_subtitle_ar}
                                                    onChangeEnglish={val => updateField('banner_subtitle', val)}
                                                    onChangeArabic={val => updateField('banner_subtitle_ar', val)}
                                                    rows={3}
                                                />
                                            </div>
                                        )}

                                        {/* ── ABOUT US ── */}
                                        {activeSection === 'about' && (
                                            <div className="space-y-6">
                                                <BilingualInput 
                                                    label="Introduction Title"
                                                    englishValue={content.Mining?.about_title}
                                                    arabicValue={content.Mining?.about_title_ar}
                                                    onChangeEnglish={val => updateField('about_title', val)}
                                                    onChangeArabic={val => updateField('about_title_ar', val)}
                                                />
                                                <BilingualTextArea 
                                                    label="Intro Description"
                                                    englishValue={content.Mining?.about_desc}
                                                    arabicValue={content.Mining?.about_desc_ar}
                                                    onChangeEnglish={val => updateField('about_desc', val)}
                                                    onChangeArabic={val => updateField('about_desc_ar', val)}
                                                    rows={6}
                                                />
                                                <div className="space-y-2">
                                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">External Website URL (English)</label>
                                                    <input className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-bold" value={content.Mining?.about_website ?? 'www.hamdanmining.com'} onChange={e => updateField('about_website', e.target.value)} />
                                                </div>
                                                <div className="grid grid-cols-1 gap-8 pt-6 border-t border-slate-100">
                                                    {[
                                                        { id: 1, def_t: 'Production Capacity', def_d: '600 TPH automated three-stage crushing plants.', def_i: 'Factory' },
                                                        { id: 2, def_t: 'HSE & Quality', def_d: 'Integrated HSE and quality management systems.', def_i: 'ShieldCheck' },
                                                        { id: 3, def_t: 'Export Markets', def_d: 'Exports to Qatar, Bahrain, Kuwait, and Bangladesh.', def_i: 'Globe2' }
                                                    ].map(pt => (
                                                        <div key={pt.id} className="space-y-4 p-6 bg-slate-50 rounded-2xl border border-slate-100">
                                                            <div className="flex justify-between items-center mb-2">
                                                                <span className="text-[10px] font-black text-blue-600 uppercase italic">Feature {pt.id}</span>
                                                                <select className="text-[10px] font-bold rounded-lg border border-slate-200 p-1" value={content.Mining?.[`about_pt_${pt.id}_icon`] ?? pt.def_i} onChange={e => updateField(`about_pt_${pt.id}_icon`, e.target.value)}>
                                                                    <option value="Factory">Factory</option>
                                                                    <option value="ShieldCheck">HSE</option>
                                                                    <option value="Globe2">Globe</option>
                                                                    <option value="Zap">Energy</option>
                                                                </select>
                                                            </div>
                                                            <BilingualInput 
                                                                label="Point Title"
                                                                englishValue={content.Mining?.[`about_pt_${pt.id}_title`]}
                                                                arabicValue={content.Mining?.[`about_pt_${pt.id}_title_ar`]}
                                                                onChangeEnglish={val => updateField(`about_pt_${pt.id}_title`, val)}
                                                                onChangeArabic={val => updateField(`about_pt_${pt.id}_title_ar`, val)}
                                                            />
                                                            <BilingualTextArea 
                                                                label="Feature Description"
                                                                englishValue={content.Mining?.[`about_pt_${pt.id}_desc`]}
                                                                arabicValue={content.Mining?.[`about_pt_${pt.id}_desc_ar`]}
                                                                onChangeEnglish={val => updateField(`about_pt_${pt.id}_desc`, val)}
                                                                onChangeArabic={val => updateField(`about_pt_${pt.id}_desc_ar`, val)}
                                                                rows={2}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* ── SERVICES ── */}
                                        {activeSection === 'services' && (
                                            <ServiceCardManager
                                                section="Mining"
                                                data={content.Mining || {}}
                                                onSaved={fetchContent}
                                                sectionTitle={content.Mining?.services_title}
                                                sectionTitleAr={content.Mining?.services_title_ar}
                                                onChangeSectionTitle={val => updateField('services_title', val)}
                                                onChangeSectionTitleAr={val => updateField('services_title_ar', val)}
                                            />
                                        )}

                                        {activeSection === 'operations' && (
                                            <div className="grid grid-cols-1 gap-8">
                                                {[
                                                    { id: 'prod', label: 'Production Capacity & Operations', icon: Factory, def: 'Hamdan Mining International owns and operates high-capacity processing facilities at the Liwa Quarry. Our primary operations focus on the production of high-quality Gabbro aggregates, sand, and rock materials.' },
                                                    { id: 'hse', label: 'Commitment to HSE & Quality', icon: ShieldCheck, def: 'We maintain rigorous health, safety, and environment standards across all our mining and processing sites. Our automated systems ensure consistent quality that meets international standards.' },
                                                    { id: 'cust', label: 'Customer Focus & Service Reliability', icon: Handshake, def: 'We prioritize long-term partnerships through consistent quality and timely delivery of materials for large-scale infrastructure projects.' },
                                                    { id: 'exp', label: 'Export & Market Coverage', icon: Ship, def: 'Our mining products are exported to major markets across the GCC, including Qatar, Bahrain, Kuwait, and South Asian markets like Bangladesh.' },
                                                ].map(op => (
                                                    <div key={op.id} className="p-8 bg-slate-50 rounded-3xl border border-slate-100 space-y-6">
                                                        <div className="flex items-center space-x-3">
                                                            <div className="w-10 h-10 bg-white rounded-xl border border-slate-200 flex items-center justify-center text-blue-600 shrink-0"><op.icon className="w-5 h-5" /></div>
                                                            <BilingualInput 
                                                                label={op.label}
                                                                englishValue={content.Mining?.[`${op.id}_title`]}
                                                                arabicValue={content.Mining?.[`${op.id}_title_ar`]}
                                                                onChangeEnglish={val => updateField(`${op.id}_title`, val)}
                                                                onChangeArabic={val => updateField(`${op.id}_title_ar`, val)}
                                                            />
                                                        </div>
                                                        <BilingualTextArea 
                                                            label="Description"
                                                            englishValue={content.Mining?.[`${op.id}_desc`]}
                                                            arabicValue={content.Mining?.[`${op.id}_desc_ar`]}
                                                            onChangeEnglish={val => updateField(`${op.id}_desc`, val)}
                                                            onChangeArabic={val => updateField(`${op.id}_desc_ar`, val)}
                                                            rows={4}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {activeSection === 'products' && (
                                            <div className="space-y-6">
                                                <p className="text-xs text-slate-400 font-bold mb-4">You can manage the 11 product grades and technical descriptions here.</p>
                                                {[
                                                    { id: 1, t: '0–2 mm Manufactured Sand (Fine Aggregate)', d: 'Precisely graded fine material suitable for asphalt production, concrete mix optimization, block manufacturing, and specialized construction applications.', c: 'Sand' },
                                                    { id: 2, t: '0–5 mm Manufactured Sand (Coarse Fine Aggregate)', d: 'Well-processed crushed sand ideal for ready-mix concrete, plastering works, pavement layers, and high-performance concrete applications requiring controlled fines content.', c: 'Sand' },
                                                    { id: 3, t: '5–10 mm Crushed Aggregate', d: 'Clean, uniformly graded aggregate used for high-strength concrete mixes, premix asphalt layers, and general construction applications requiring fine coarse aggregates.', c: 'Aggregate' },
                                                    { id: 4, t: '10–20 mm Crushed Aggregate', d: 'Standard coarse aggregate widely used in structural concrete, road base reinforcement, and infrastructure projects requiring consistent particle size and angularity.', c: 'Aggregate' },
                                                    { id: 5, t: '20–28 mm Crushed Aggregate', d: 'Intermediate-sized aggregate suitable for heavy-duty concrete works, drainage layers, and load-bearing structural applications.', c: 'Aggregate' },
                                                    { id: 6, t: '20–40 mm Crushed Aggregate', d: 'Large-sized aggregate designed for mass concrete structures, foundation works, and applications requiring enhanced void ratios and superior drainage performance.', c: 'Aggregate' },
                                                    { id: 7, t: 'Engineered Backfill Material', d: 'Well-graded and compactable material used for structural backfilling, trench filling, retaining wall support, and civil engineering works requiring controlled compaction properties.', c: 'Road Materials' },
                                                    { id: 8, t: 'Subgrade Material', d: 'Prepared natural/crushed material used to improve ground bearing capacity, provide uniform support for pavement structures, and enhance long-term soil stability.', c: 'Road Materials' },
                                                    { id: 9, t: 'Granular Sub-Base (GSB) Material', d: 'High-quality, mechanically crushed granular material used as a foundational layer in road construction, providing load dispersion, drainage, and structural support for upper pavement layers.', c: 'Road Materials' },
                                                    { id: 10, t: 'Armour Rock (Riprap)', d: 'Heavy-duty, large-dimension rock blocks used for shoreline protection, breakwaters, revetments, and erosion control systems requiring high durability and mass stability.', c: 'Rock & Filter' },
                                                    { id: 11, t: 'Filter Material', d: 'Carefully graded aggregate used in drainage systems, geotechnical filtration layers, and hydraulic structures to prevent soil migration while permitting controlled water flow.', c: 'Rock & Filter' }
                                                ].map(p => (
                                                    <div key={p.id} className="p-8 bg-slate-50 rounded-2xl border border-slate-100 space-y-6">
                                                        <div className="grid grid-cols-1 gap-6">
                                                            <BilingualInput 
                                                                label="Product / Grade Title"
                                                                englishValue={content.Mining?.[`prod_${p.id}_title`]}
                                                                arabicValue={content.Mining?.[`prod_${p.id}_title_ar`]}
                                                                onChangeEnglish={val => updateField(`prod_${p.id}_title`, val)}
                                                                onChangeArabic={val => updateField(`prod_${p.id}_title_ar`, val)}
                                                            />
                                                            <div className="space-y-2">
                                                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Category (English)</label>
                                                                <select className="w-full p-4 bg-white border border-slate-200 rounded-xl font-bold text-sm" value={content.Mining?.[`prod_${p.id}_cat`] ?? p.c} onChange={e => updateField(`prod_${p.id}_cat`, e.target.value)}>
                                                                    <option value="Sand">Sand</option>
                                                                    <option value="Aggregate">Aggregate</option>
                                                                    <option value="Road Materials">Road Materials</option>
                                                                    <option value="Rock & Filter">Rock & Filter</option>
                                                                </select>
                                                            </div>
                                                        </div>
                                                        <BilingualTextArea 
                                                            label="Technical description and specifications..."
                                                            englishValue={content.Mining?.[`prod_${p.id}_desc`]}
                                                            arabicValue={content.Mining?.[`prod_${p.id}_desc_ar`]}
                                                            onChangeEnglish={val => updateField(`prod_${p.id}_desc`, val)}
                                                            onChangeArabic={val => updateField(`prod_${p.id}_desc_ar`, val)}
                                                            rows={2}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {activeSection === 'clients' && (
                                            <ClientsEditorFields
                                                data={content.Mining}
                                                updateField={updateField}
                                                defaultDescription="Hamdan Mining International has supplied high-quality materials to a wide range of companies and organizations."
                                            />
                                        )}

                                        {activeSection === 'gallery' && (() => {
                                            const miningData = content.Mining || {};
                                            const galleryEntries = (Object.entries(miningData) as [string, string][])
                                                .filter(([k, v]) => 
                                                    k.startsWith('gallery_img_') && 
                                                    !k.endsWith('_ar') &&
                                                    v && v.trim() !== ''
                                                )
                                                .sort((a, b) => a[0].localeCompare(b[0]));

                                            return (
                                                <div className="space-y-8">
                                                    <div className="space-y-6 bg-slate-50 p-6 rounded-2xl border border-slate-100">
                                                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Section Header Details</h4>
                                                        <BilingualInput 
                                                            label="Gallery Title"
                                                            englishValue={content.Mining?.gallery_title}
                                                            arabicValue={content.Mining?.gallery_title_ar}
                                                            onChangeEnglish={val => updateField('gallery_title', val)}
                                                            onChangeArabic={val => updateField('gallery_title_ar', val)}
                                                        />
                                                        <BilingualInput 
                                                            label="Gallery Subtitle"
                                                            englishValue={content.Mining?.gallery_subtitle}
                                                            arabicValue={content.Mining?.gallery_subtitle_ar}
                                                            onChangeEnglish={val => updateField('gallery_subtitle', val)}
                                                            onChangeArabic={val => updateField('gallery_subtitle_ar', val)}
                                                        />
                                                    </div>

                                                    {/* Gallery Image Manager Header */}
                                                    <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-slate-200">
                                                        <div>
                                                            <h4 className="text-lg font-black text-slate-800">Gallery Image Collection ({galleryEntries.length} Custom Images)</h4>
                                                            <p className="text-xs font-medium text-slate-500">Upload new images, edit image URLs/paths, or delete images.</p>
                                                        </div>
                                                        <div className="flex flex-wrap items-center gap-3">
                                                            <label className="cursor-pointer flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-xs transition-all shadow-md">
                                                                <Plus className="w-4 h-4" />
                                                                <span>Upload Image(s)</span>
                                                                <input
                                                                    type="file"
                                                                    multiple
                                                                    accept="image/*"
                                                                    className="hidden"
                                                                    onChange={async (e) => {
                                                                        const files = Array.from(e.target.files || []);
                                                                        for (let i = 0; i < files.length; i++) {
                                                                            const key = `gallery_img_${Date.now()}_${i}`;
                                                                            await handleUploadGalleryImage(key, files[i]);
                                                                        }
                                                                    }}
                                                                />
                                                            </label>
                                                            <button
                                                                onClick={handleAddGalleryImage}
                                                                className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold text-xs transition-all border border-slate-200"
                                                            >
                                                                <Plus className="w-4 h-4" />
                                                                <span>Add URL Row</span>
                                                            </button>
                                                            {galleryEntries.length === 0 && (
                                                                <button
                                                                    onClick={() => {
                                                                        defaultMiningImages.forEach((imgUrl, idx) => {
                                                                            updateField(`gallery_img_${String(idx + 1).padStart(3, '0')}`, imgUrl);
                                                                        });
                                                                    }}
                                                                    className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-xl font-bold text-xs transition-all border border-amber-200"
                                                                >
                                                                    <RefreshCw className="w-4 h-4" />
                                                                    <span>Import Default Photos</span>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Gallery Cards Grid */}
                                                    {galleryEntries.length === 0 ? (
                                                        <div className="text-center py-16 bg-slate-50 rounded-3xl border border-dashed border-slate-200">
                                                            <ImageIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                                                            <p className="text-sm font-bold text-slate-600">No custom gallery images saved in database yet</p>
                                                            <p className="text-xs text-slate-400 mt-1">Click "Upload Image(s)", "Add URL Row", or "Import Default Photos" to customize gallery photos.</p>
                                                        </div>
                                                    ) : (
                                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                                            {galleryEntries.map(([key, imgUrl]) => (
                                                                <div key={key} className="p-4 bg-slate-50/70 rounded-2xl border border-slate-200/80 space-y-3 relative group">
                                                                    <div className="relative aspect-video rounded-xl overflow-hidden bg-slate-900 border border-slate-200">
                                                                        <img
                                                                            src={imgUrl}
                                                                            alt={key}
                                                                            className="w-full h-full object-cover"
                                                                            onError={(e) => {
                                                                                (e.currentTarget as HTMLImageElement).src = 'https://via.placeholder.com/400x225?text=Invalid+Image+URL';
                                                                            }}
                                                                        />
                                                                        <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-md px-2.5 py-1 rounded-full text-[10px] font-mono text-white/80 border border-white/10">
                                                                            {key}
                                                                        </div>
                                                                        <button
                                                                            onClick={() => handleDeleteGalleryKey(key)}
                                                                            className="absolute top-2 right-2 p-2 bg-red-600 hover:bg-red-700 text-white rounded-full transition-all shadow-lg active:scale-95"
                                                                            title="Delete Image"
                                                                        >
                                                                            <Trash2 className="w-4 h-4" />
                                                                        </button>
                                                                    </div>

                                                                    <div className="space-y-2">
                                                                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Image Source URL / Path</label>
                                                                        <div className="flex items-center gap-2">
                                                                            <input
                                                                                type="text"
                                                                                value={imgUrl}
                                                                                onChange={(e) => updateField(key, e.target.value)}
                                                                                className="flex-1 px-3 py-2 bg-white rounded-xl border border-slate-200 text-xs font-mono text-slate-800 focus:outline-none focus:border-blue-600"
                                                                                placeholder="/mining/image.jpg or https://..."
                                                                            />
                                                                            <label className="cursor-pointer p-2 bg-white hover:bg-slate-100 text-slate-600 rounded-xl border border-slate-200 transition-colors" title="Replace File">
                                                                                <Upload className="w-4 h-4" />
                                                                                <input
                                                                                    type="file"
                                                                                    accept="image/*"
                                                                                    className="hidden"
                                                                                    onChange={(e) => {
                                                                                        const file = e.target.files?.[0];
                                                                                        if (file) handleUploadGalleryImage(key, file);
                                                                                    }}
                                                                                />
                                                                            </label>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })()}

                                        {activeSection === 'contact' && (
                                            <div className="grid grid-cols-1 gap-8">
                                                <div className="space-y-6">
                                                    <BilingualInput 
                                                        label="Form Title"
                                                        englishValue={content.Mining?.contact_form_title}
                                                        arabicValue={content.Mining?.contact_form_title_ar}
                                                        onChangeEnglish={val => updateField('contact_form_title', val)}
                                                        onChangeArabic={val => updateField('contact_form_title_ar', val)}
                                                    />
                                                    <BilingualInput 
                                                        label="Info Title"
                                                        englishValue={content.Mining?.contact_info_title}
                                                        arabicValue={content.Mining?.contact_info_title_ar}
                                                        onChangeEnglish={val => updateField('contact_info_title', val)}
                                                        onChangeArabic={val => updateField('contact_info_title_ar', val)}
                                                    />
                                                    <BilingualTextArea 
                                                        label="Info Description"
                                                        englishValue={content.Mining?.contact_info_desc}
                                                        arabicValue={content.Mining?.contact_info_desc_ar}
                                                        onChangeEnglish={val => updateField('contact_info_desc', val)}
                                                        onChangeArabic={val => updateField('contact_info_desc_ar', val)}
                                                        rows={4}
                                                    />
                                                </div>
                                                <div className="bg-slate-50 p-8 rounded-3xl border border-slate-100 space-y-6">
                                                    <span className="text-[10px] font-black text-blue-600 uppercase">Head Office Details</span>
                                                    <div className="space-y-6">
                                                        <BilingualInput 
                                                            label="Address"
                                                            englishValue={content.Mining?.contact_address}
                                                            arabicValue={content.Mining?.contact_address_ar}
                                                            onChangeEnglish={val => updateField('contact_address', val)}
                                                            onChangeArabic={val => updateField('contact_address_ar', val)}
                                                        />
                                                        <BilingualInput 
                                                            label="Phone"
                                                            englishValue={content.Mining?.contact_phone}
                                                            arabicValue={content.Mining?.contact_phone_ar}
                                                            onChangeEnglish={val => updateField('contact_phone', val)}
                                                            onChangeArabic={val => updateField('contact_phone_ar', val)}
                                                        />
                                                        <BilingualInput 
                                                            label="Fax"
                                                            englishValue={content.Mining?.contact_fax}
                                                            arabicValue={content.Mining?.contact_fax_ar}
                                                            onChangeEnglish={val => updateField('contact_fax', val)}
                                                            onChangeArabic={val => updateField('contact_fax_ar', val)}
                                                        />
                                                        <BilingualInput 
                                                            label="Email"
                                                            englishValue={content.Mining?.contact_email}
                                                            arabicValue={content.Mining?.contact_email_ar}
                                                            onChangeEnglish={val => updateField('contact_email', val)}
                                                            onChangeArabic={val => updateField('contact_email_ar', val)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* == BRANCHES SECTION == */}
                                        {activeSection === 'branches' && (() => {
                                            const M = content.Mining || {};
                                            const getBranches = () => {
                                                return activeMiningBranches.map(i => ({
                                                    i,
                                                    name: M[`br_${i}_name`] || '',
                                                    addr: M[`br_${i}_addr`] || '',
                                                    phone: M[`br_${i}_phone`] || '',
                                                    fax: M[`br_${i}_fax`] || '',
                                                    coord: M[`br_${i}_coord`] || ''
                                                }));
                                            };
                                            const branches = getBranches();
                                            const nextI = activeMiningBranches.length > 0 ? Math.max(...activeMiningBranches) + 1 : 1;
                                            const handleAdd = () => {
                                                updateField(`br_${nextI}_name`, 'New Branch');
                                                updateField(`br_${nextI}_addr`, '');
                                                updateField(`br_${nextI}_phone`, '');
                                                updateField(`br_${nextI}_fax`, '');
                                                updateField(`br_${nextI}_coord`, '');
                                                setActiveMiningBranches(prev => [...prev, nextI]);
                                            };
                                            const handleDelete = (idx: number) => {
                                                if (!window.confirm('Delete this branch?')) return;
                                                ['name', 'addr', 'phone', 'fax', 'coord'].forEach(f => updateField(`br_${idx}_${f}`, ''));
                                                setActiveMiningBranches(prev => prev.filter(id => id !== idx));
                                            };
                                            return (
                                                <div className="space-y-4">
                                                    <div className="flex items-center justify-between">
                                                        <div className="bg-blue-50 p-4 rounded-2xl border border-blue-100 flex-1 mr-4">
                                                            <p className="text-xs font-bold text-blue-700">{branches.length} branch(es) — Edit inline, then click "Update branches" to save.</p>
                                                        </div>
                                                        <button onClick={handleAdd} className="flex items-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-sm transition-all shadow-lg">
                                                            <span className="text-lg leading-none">+</span> Add Branch
                                                        </button>
                                                    </div>
                                                    {branches.length === 0 && (
                                                        <div className="text-center py-12 text-slate-400">
                                                            <p className="text-sm font-bold">No branches configured yet.</p>
                                                            <p className="text-xs mt-1">Click "Add Branch" to get started.</p>
                                                        </div>
                                                    )}
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                                        {branches.map((branch) => (
                                                            <div key={branch.i} className="p-6 rounded-2xl border border-slate-100 bg-slate-50/50 space-y-4">
                                                                <div className="flex justify-between items-center">
                                                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">BRANCH #{branch.i}</span>
                                                                    <button onClick={() => handleDelete(branch.i)} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl text-[10px] font-black transition-all">🗑 Delete</button>
                                                                </div>
                                                                <BilingualInput label="Branch Name" englishValue={M[`br_${branch.i}_name`] || ''} arabicValue={M[`br_${branch.i}_name_ar`]} onChangeEnglish={val => updateField(`br_${branch.i}_name`, val)} onChangeArabic={val => updateField(`br_${branch.i}_name_ar`, val)} />
                                                                <BilingualTextArea label="Address" englishValue={M[`br_${branch.i}_addr`] || ''} arabicValue={M[`br_${branch.i}_addr_ar`]} onChangeEnglish={val => updateField(`br_${branch.i}_addr`, val)} onChangeArabic={val => updateField(`br_${branch.i}_addr_ar`, val)} rows={2} />
                                                                <div className="grid grid-cols-2 gap-4">
                                                                    <BilingualInput label="Phone" englishValue={M[`br_${branch.i}_phone`] || ''} arabicValue={M[`br_${branch.i}_phone_ar`]} onChangeEnglish={val => updateField(`br_${branch.i}_phone`, val)} onChangeArabic={val => updateField(`br_${branch.i}_phone_ar`, val)} />
                                                                    <BilingualInput label="Fax" englishValue={M[`br_${branch.i}_fax`] || ''} arabicValue={M[`br_${branch.i}_fax_ar`]} onChangeEnglish={val => updateField(`br_${branch.i}_fax`, val)} onChangeArabic={val => updateField(`br_${branch.i}_fax_ar`, val)} />
                                                                </div>
                                                                <BilingualInput label="GPS Coordinates" englishValue={M[`br_${branch.i}_coord`] || ''} arabicValue={M[`br_${branch.i}_coord_ar`]} onChangeEnglish={val => updateField(`br_${branch.i}_coord`, val)} onChangeArabic={val => updateField(`br_${branch.i}_coord_ar`, val)} placeholder="e.g. 23.575480, 58.354941" />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                    </div>
                                </motion.div>
                            ) : (
                                <motion.div key="images" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="space-y-8">
                                    {message.text && (
                                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className={`p-4 rounded-2xl font-bold text-sm flex items-center gap-3 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                                            {message.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                                            {message.text}
                                        </motion.div>
                                    )}
                                    <div className="flex items-center justify-between mb-6">
                                        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex-1 mr-6">
                                            <h3 className="text-xl font-black text-slate-800 tracking-tight">Media Management</h3>
                                            <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">Global Batch Uploader</p>
                                        </div>
                                        <button
                                            onClick={saveImages}
                                            disabled={Object.keys(pendingImages).length === 0 || !!saving}
                                            className="px-10 py-5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 text-white rounded-3xl font-black text-xs uppercase tracking-widest transition-all shadow-2xl shadow-blue-100 flex items-center space-x-3"
                                        >
                                            {saving === 'images' ? <Loader2 className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" />}
                                            <span>Synchronize {Object.keys(pendingImages).length} Assets</span>
                                        </button>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        <UploadBox keyName="banner_1" label="Hero Banner 01" currentImage={content.Mining?.banner_1 ?? '/banner/mining_1.jpg'} />
                                        <UploadBox keyName="banner_2" label="Hero Banner 02" currentImage={content.Mining?.banner_2 ?? '/banner/mining_2.jpg'} />
                                        <UploadBox keyName="banner_3" label="Hero Banner 03" currentImage={content.Mining?.banner_3 ?? '/banner/mining_4.jpg'} />
                                        <UploadBox keyName="about_image" label="Production Site Image" currentImage={content.Mining?.about_image ?? '/Hamdan-Mining-logo.jpg'} />
                                        <UploadBox keyName="svc_1_image" label="Service 1 Image" currentImage={content.Mining?.svc_1_image} />
                                        <UploadBox keyName="svc_2_image" label="Service 2 Image" currentImage={content.Mining?.svc_2_image} />
                                        <UploadBox keyName="svc_3_image" label="Service 3 Image" currentImage={content.Mining?.svc_3_image} />
                                    </div>

                                    <div className="mt-12">
                                        <ClientLogoUploadGrid
                                            data={content.Mining}
                                            pendingImages={pendingImages}
                                            onSelect={handleImageSelect}
                                        />
                                    </div>

                                    <div className="mt-12 bg-blue-50 border border-blue-200 rounded-3xl p-6 flex items-center gap-4">
                                        <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center shrink-0">
                                            <ImageIcon className="w-5 h-5 text-white" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-blue-900">Gallery images are managed in the <span className="underline">Section Content → Gallery</span> tab.</p>
                                            <p className="text-xs text-blue-600 mt-0.5">Go there to add, edit, or delete gallery photos.</p>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default AdminMining;
